import { useState, useCallback } from 'react';
import { generateQuotePDF } from '../utils/pdfGenerator';
import { validateQuoteForSubmission } from '../config';

/**
 * Custom hook for quote persistence operations
 * Handles saving drafts, finalizing quotes, and PDF generation
 * 
 * ⭐ H2: Added validation before final quote submission
 * 
 * @param {Object} options - Configuration options
 * @param {Object} options.quoteData - Current quote data
 * @param {Function} options.calculateLineItems - Function to calculate line items
 * @param {Object} options.totals - Calculated totals object
 * @param {number} options.currentStep - Current wizard step
 * @param {string|null} options.currentQuoteId - Current quote ID (null for new quotes)
 * @param {Function} options.setCurrentQuoteId - Setter for quote ID
 * @param {Function} options.setQuoteNumber - Setter for quote number
 * @param {Function} options.setView - Setter for view state
 * @returns {Object} - Persistence functions and state
 */
const useQuotePersistence = ({
  quoteData,
  calculateLineItems,
  totals,
  currentStep,
  currentQuoteId,
  setCurrentQuoteId,
  setQuoteNumber,
  setView
}) => {
  // Loading states
  const [saving, setSaving] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  
  // Success notification states
  const [showSuccess, setShowSuccess] = useState(false);
  const [showDraftSaved, setShowDraftSaved] = useState(false);

  // API URL from environment
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  /**
   * Build the payload for API requests
   * @param {string} status - 'draft' or 'final'
   * @returns {Object} - API payload
   */
  const buildPayload = useCallback((status) => ({
    customerName: quoteData.customerName,
    industry: quoteData.industry,
    region: quoteData.region,
    opportunityId: quoteData.opportunityId,
    status,
    currentStep,
    quoteData: {
      ...quoteData,
      lineItems: calculateLineItems(),
      totals
    }
  }), [quoteData, currentStep, calculateLineItems, totals]);

  /**
   * Save quote to API (create or update)
   * @param {Object} payload - Quote payload
   * @returns {Promise<Object>} - API response data
   */
  const saveToAPI = useCallback(async (payload) => {
    const isUpdate = currentQuoteId !== null;
    const url = isUpdate
      ? `${API_URL}/api/quotes/${currentQuoteId}`
      : `${API_URL}/api/quotes`;

    // Include auth token in headers
    const token = localStorage.getItem('authToken');
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };

    const response = await fetch(url, {
      method: isUpdate ? 'PUT' : 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to save quote (${response.status})`);
    }

    return response.json();
  }, [API_URL, currentQuoteId]);

  /**
   * Save quote as draft
   * Validates customer name before saving
   */
  const handleSaveDraft = useCallback(async () => {
    if (!quoteData.customerName.trim()) {
      alert('Please enter a customer name before saving');
      return { success: false, error: 'Customer name required' };
    }

    setSavingDraft(true);
    try {
      const payload = buildPayload('draft');
      const data = await saveToAPI(payload);
      
      setQuoteNumber(data.quote.quote_number);
      setCurrentQuoteId(data.quote.id);
      setShowDraftSaved(true);
      
      setTimeout(() => {
        setShowDraftSaved(false);
        setView('list');
      }, 2000);
      
      console.log('Draft saved:', data);
      return { success: true, data };
    } catch (error) {
      console.error('Error saving draft:', error);
      alert('Failed to save draft. Please try again.');
      return { success: false, error: error.message };
    } finally {
      setSavingDraft(false);
    }
  }, [quoteData.customerName, buildPayload, saveToAPI, setQuoteNumber, setCurrentQuoteId, setView]);

  /**
   * Submit quote for approval (with threshold checking)
   * If discounts exceed thresholds, quote goes to pending_approval
   * Otherwise, it's auto-approved and set to final
   */
  const handleSubmitQuote = useCallback(async () => {
    // Validate quote before submission
    const validation = validateQuoteForSubmission(quoteData);
    if (!validation.valid) {
      const errorMessage = 'Cannot submit quote. Please fix the following:\n\n' +
        validation.errors.map(e => `• ${e}`).join('\n');
      alert(errorMessage);
      return { success: false, error: 'Validation failed', errors: validation.errors };
    }

    setSaving(true);
    try {
      // First save the quote as draft to ensure it's in the database
      const payload = buildPayload('draft');
      const saveData = await saveToAPI(payload);

      const quoteId = saveData.quote.id;
      setQuoteNumber(saveData.quote.quote_number);
      setCurrentQuoteId(quoteId);

      // Now submit for approval
      const token = localStorage.getItem('authToken');
      const headers = {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      };

      const submitResponse = await fetch(`${API_URL}/api/quotes/${quoteId}/submit`, {
        method: 'POST',
        headers,
        credentials: 'include'
      });

      if (!submitResponse.ok) {
        const errorData = await submitResponse.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to submit quote');
      }

      const submitData = await submitResponse.json();

      if (submitData.requiresApproval) {
        // Quote needs approval
        const breachMessage = submitData.breaches
          .map(b => `• ${b.category}: ${b.discountApplied}% (max allowed: ${b.maxAllowed}%)`)
          .join('\n');

        alert(`Quote submitted for approval.\n\nDiscounts exceeding thresholds:\n${breachMessage}\n\nA manager or admin will review your quote.`);
      } else {
        // Auto-approved
        setShowSuccess(true);
        setTimeout(() => {
          setShowSuccess(false);
        }, 3000);
      }

      setTimeout(() => {
        setView('list');
      }, 2000);

      console.log('Quote submitted:', submitData);
      return { success: true, data: submitData };
    } catch (error) {
      console.error('Error submitting quote:', error);
      alert(`Failed to submit quote: ${error.message}`);
      return { success: false, error: error.message };
    } finally {
      setSaving(false);
    }
  }, [quoteData, buildPayload, saveToAPI, setQuoteNumber, setCurrentQuoteId, setView, API_URL]);

  /**
   * Generate and save final quote (legacy - now uses submit flow)
   * ⭐ H2: Validates quote before submission
   */
  const handleGenerateQuote = useCallback(async () => {
    // Use the new submit flow which checks thresholds
    return handleSubmitQuote();
  }, [handleSubmitQuote]);

  /**
   * Generate and download PDF
   * @param {string} type - 'internal' or 'customer'
   * @param {string} quoteNumber - Quote number for the PDF
   * @param {string} quoteId - Quote ID (for marking as sent when customer PDF)
   * @param {Function} onStatusChange - Callback when status changes to 'sent'
   */
  const handleDownloadPDF = useCallback(async (type = 'internal', quoteNumber = 'DRAFT', quoteId = null, onStatusChange = null) => {
    setGeneratingPDF(true);
    try {
      const quoteForPDF = {
        quote_number: quoteNumber,
        customer_name: quoteData.customerName,
        customer_industry: quoteData.industry,
        customer_region: quoteData.region,
        opportunity_id: quoteData.opportunityId,
        status: 'final',
        created_at: new Date().toISOString(),
        quote_data: {
          ...quoteData,
          lineItems: calculateLineItems(),
          totals
        }
      };

      generateQuotePDF(quoteForPDF, type);

      // If generating Customer PDF and we have a quote ID, mark as sent
      if (type === 'customer' && quoteId) {
        try {
          const token = localStorage.getItem('authToken');
          const response = await fetch(`${API_URL}/api/quotes/${quoteId}/mark-sent`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token && { 'Authorization': `Bearer ${token}` })
            },
            credentials: 'include'
          });

          if (response.ok) {
            console.log('Quote marked as sent');
            if (onStatusChange) {
              onStatusChange('sent');
            }
          }
        } catch (err) {
          console.error('Failed to mark quote as sent:', err);
        }
      }

      return { success: true };
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
      return { success: false, error: error.message };
    } finally {
      setGeneratingPDF(false);
    }
  }, [quoteData, calculateLineItems, totals, API_URL]);

  /**
   * Reset all success notifications
   */
  const resetNotifications = useCallback(() => {
    setShowSuccess(false);
    setShowDraftSaved(false);
  }, []);

  return {
    // Loading states
    saving,
    savingDraft,
    generatingPDF,

    // Notification states
    showSuccess,
    showDraftSaved,

    // Actions
    handleSaveDraft,
    handleGenerateQuote,
    handleSubmitQuote,
    handleDownloadPDF,
    resetNotifications,

    // Utilities
    buildPayload
  };
};

export default useQuotePersistence;
